/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import type { components } from '@/ts/api/core/schema.generated.d.ts'

import { Entity } from '@/ts/entities/base/entity.ts'
import { Identifier } from '@/ts/entities/base/identifier.ts'

export type APIMinimalUser = components['schemas']['ShortUsers']

export type APIUser = components['schemas']['User']

type DepartmentAssignments = Record<number, boolean>

function parseDepartmentAssignments(deptAssignments: APIMinimalUser['department_assignments']): DepartmentAssignments {
  return deptAssignments.reduce((acc, v) => {
    acc[v.department_id] = v.is_assignable
    return acc
  }, {} as DepartmentAssignments)
}

function serializeDepartmentAssignments(
  deptAssignments: DepartmentAssignments
): APIMinimalUser['department_assignments'] {
  return Object.entries(deptAssignments).map(([deptId, isAssignable]) => ({
    department_id: deptId as unknown as number,
    is_assignable: isAssignable,
  }))
}

export class MinimalUser extends Entity<APIMinimalUser> {
  constructor(
    id: Identifier,
    public username: string,
    public firstName: string | undefined,
    public lastName: string | undefined,
    public pronouns: string[],
    public departmentAssignments: DepartmentAssignments
  ) {
    super(id)
  }

  getFullName(): string {
    if (!this.firstName && !this.lastName) return this.username
    if (!this.firstName) return this.lastName!
    if (!this.lastName) return this.firstName
    return `${this.firstName} ${this.lastName}`
  }

  toFormDisplayValue(): string {
    return this.username ?? this.getFullName()
  }

  toJSON() {
    return {
      id: this.id.valueOf(),
      username: this.username,
      first_name: this.firstName,
      last_name: this.lastName,
      department_assignments: serializeDepartmentAssignments(this.departmentAssignments),
    }
  }

  static fromJSON(user: APIMinimalUser) {
    const newUser = new MinimalUser(
      new Identifier(user.id),
      user.username,
      user.first_name,
      user.last_name,
      user.pronouns ?? [],
      parseDepartmentAssignments(user.department_assignments)
    )
    return newUser
  }
}

export class User extends MinimalUser implements Entity<APIUser> {
  constructor(
    id: Identifier,
    username: string,
    firstName: string | undefined,
    lastName: string | undefined,
    pronouns: string[],
    public email: string,
    departmentAssignments: DepartmentAssignments,
    public roles: number[],
    public groups: number[] | undefined,
    public permissions: components['schemas']['UserPermissionsSerializers'][],
    public legalFistName: string,
    public legalLastName: string
  ) {
    super(id, username, firstName, lastName, pronouns, departmentAssignments)
  }

  toJSON() {
    return {
      id: this.id.valueOf(),
      username: this.username,
      first_name: this.firstName,
      last_name: this.lastName,
      pronouns: this.pronouns,
      email: this.email,
      groups: this.groups,
      department_assignments: serializeDepartmentAssignments(this.departmentAssignments),
      roles: this.roles,
      permissions: this.permissions,
    }
  }

  static fromJSON(user: APIUser) {
    return new User(
      new Identifier(user.id),
      user.username,
      user.first_name,
      user.last_name,
      user.pronouns ?? [],
      user.email,
      parseDepartmentAssignments(user.department_assignments),
      user.roles,
      user.groups,
      user.permissions,
      user.legal_first_name ?? '',
      user.legal_last_name ?? ''
    )
  }
}
